import React, { useState, useEffect, useRef } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  Users, Truck, Package,
  ClipboardText, Receipt, ShoppingCart,
  Buildings, UserCircle, SignOut, FileText, Gauge, Money,
  ArrowUUpLeft, Bell, Target, ChartBar, Stack, ShieldCheck, Wrench, Vault, Note, Calculator,
  HourglassMedium, Key, WhatsappLogo, Storefront, Database,
  List, IdentificationCard
} from "@phosphor-icons/react";import { useAuth } from "@/contexts/AuthContext";
import VetrichLogo from "@/components/VetrichLogo";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  Sheet, SheetContent
} from "@/components/ui/sheet";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: Gauge, testid: "nav-dashboard" },
  { to: "/customers", label: "Customers", icon: Users, testid: "nav-customers", perm: "customers.view" },
  { to: "/suppliers", label: "Suppliers", icon: Truck, testid: "nav-suppliers", perm: "suppliers.view" },
  { to: "/products", label: "Products", icon: Package, testid: "nav-products", perm: "products.view" },
  { to: "/services", label: "Services", icon: Wrench, testid: "nav-services", perm: "services.view" },
  { to: "/stock", label: "Stock", icon: Stack, testid: "nav-stock", perm: "stock.view" },
  { to: "/quotations", label: "Quotations", icon: ClipboardText, testid: "nav-quotations", perm: "quotations.view" },
  { to: "/sales", label: "Sales Invoices", icon: Receipt, testid: "nav-sales", perm: "sales.view" },
  { to: "/returns", label: "Sales Returns", icon: ArrowUUpLeft, testid: "nav-returns", perm: "returns.view" },
  { to: "/purchases", label: "Purchases", icon: ShoppingCart, testid: "nav-purchases", perm: "purchases.view" },
  { to: "/payments", label: "Debit & Credit Vouchers", icon: Money, testid: "nav-payments", perm: "payments.view" },
  { to: "/my-hrms", label: "HRMS", icon: IdentificationCard, testid: "nav-my-hrms", roles: ["employee", "manager", "regional_manager", "hr"] },
  { to: "/cashbook", label: "Cash Book", icon: Vault, testid: "nav-cashbook", perm: "cash_book.view" },
  { to: "/daily-close", label: "Daily Close", icon: Calculator, testid: "nav-daily-close", perm: "daily_close.view" },
  { to: "/cheques", label: "Cheques", icon: Note, testid: "nav-cheques", perm: "payments.view" },
  { to: "/pending-payments", label: "Payment Pending", icon: HourglassMedium, testid: "nav-pending-payments", perm: "sales.view" },
  { to: "/pending-works", label: "Add Daily Pending Works", icon: ClipboardText, testid: "nav-pending-works", perm: "pending_works.view" },
  { to: "/dsc", label: "DSC Register", icon: ShieldCheck, testid: "nav-dsc", perm: "dsc.view" },
  { to: "/dsc-list", label: "DSC List Serial Number", icon: ShieldCheck, testid: "nav-dsc-list", perm: "dsc.view" },
  { to: "/outward-inward", label: "Outward / Inward", icon: ArrowUUpLeft, testid: "nav-outward-inward", perm: "outward_inward.view" },
  { to: "/credentials", label: "User ID / Password", icon: ShieldCheck, testid: "nav-credentials", perm: "credentials.view" },
  { to: "/reminders", label: "Reminders", icon: Bell, testid: "nav-reminders", perm: "reminders.view" },
  { to: "/performance", label: "Performance", icon: Target, testid: "nav-performance", perm: "targets.view" },
  { to: "/reports", label: "Reports", icon: ChartBar, testid: "nav-reports", perm: "reports.view" },
  { to: "/gst", label: "GST Reports", icon: FileText, testid: "nav-gst", perm: "gst.view", requiresGst: true },
];
const ADMIN_NAV = [
  { to: "/companies", label: "Companies", icon: Buildings, testid: "nav-companies", roles: ["admin"] },
  { to: "/stores", label: "Stores", icon: Storefront, testid: "nav-stores", roles: ["admin"] },
  { to: "/users", label: "Users", icon: UserCircle, testid: "nav-users", roles: ["admin", "manager", "regional_manager", "hr"] },
  { to: "/hrms", label: "HRMS", icon: IdentificationCard, testid: "nav-hrms", roles: ["admin"] },
  { to: "/permissions", label: "Permissions", icon: ShieldCheck, testid: "nav-permissions", roles: ["admin"] },
  { to: "/settings/integrations", label: "Integrations", icon: Key, testid: "nav-integrations", roles: ["admin"] },
  { to: "/backup", label: "Data Backup", icon: Database, testid: "nav-backup", roles: ["admin"] },
];

function NavList({ user, can, isGstCompany, onNavigate }) {
  return (
    <nav className="flex-1 px-3 py-4 overflow-y-auto">
      <div className="eyebrow px-3 mb-2">Operations</div>
      <div className="flex flex-col gap-0.5">
        {NAV.filter((n) => {
          if (user?.role === "hr") return !!n.roles?.includes("hr");
          if (n.roles && !n.roles.includes(user?.role)) return false;
          if (n.requiresGst && !isGstCompany) return false;
          if (!n.perm) return true;
          if (user?.role === "admin") return true;
          const [m, a] = n.perm.split(".");
          return can(m, a);
        }).map((n) => (
          <NavLink key={n.to} to={n.to} data-testid={n.testid} onClick={onNavigate}
            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
            <n.icon weight="regular" size={17} />
            <span>{n.label}</span>
          </NavLink>
        ))}
      </div>
      <div className="eyebrow px-3 mt-6 mb-2">Administration</div>
      <div className="flex flex-col gap-0.5">
        {ADMIN_NAV.filter((n) => !n.roles || n.roles.includes(user?.role)).map((n) => (
          <NavLink key={n.to} to={n.to} data-testid={n.testid} onClick={onNavigate}
            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
            <n.icon weight="regular" size={17} />
            <span>{n.label}</span>
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

export default function AppLayout() {
  const { user, companies, activeCompanyId, setActiveCompanyId, stores, activeStoreId, setActiveStoreId, logout, can, isGstCompany } = useAuth();
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // ---------- DSC expiry pop-up (15 days) ----------
  // Fires once per company/day: lists customers whose DSC expires within 15 days.
  const lastDscAlert = useRef(0);
  useEffect(() => {
    if (!activeCompanyId || user?.role === "hr") return;
    let cancelled = false;
    const check = async () => {
      try {
        const { data } = await api.get("/dsc/expiring?days=15");
        if (cancelled) return;
        // Per-day dedup key — stores only "1", no PII / tokens (auth is httpOnly cookie-based)
        const key = `dsc-alert-${activeCompanyId}-${new Date().toISOString().slice(0,10)}`;
        if (data.count > 0 && localStorage.getItem(key) !== "1") {
          localStorage.setItem(key, "1");
          lastDscAlert.current = Date.now();
          const names = data.items.slice(0, 5).map((i) => `${i.customer_name} (${i.days_left}d)`).join(", ");
          const more = data.items.length > 5 ? ` +${data.items.length - 5} more` : "";
          toast.warning(
            `${data.count} DSC expiring within 15 days: ${names}${more}`,
            {
              duration: 15000,
              action: { label: "Open register", onClick: () => navigate("/dsc") },
            }
          );
        }
      } catch { /* ignore */ }
    };
    const initial = setTimeout(check, 12000);
    // Re-check every 6 hours (localStorage guard prevents duplicate same-day toasts)
    const timer = setInterval(check, 6 * 60 * 60 * 1000);
    return () => { cancelled = true; clearTimeout(initial); clearInterval(timer); };
  }, [activeCompanyId, navigate, user?.role]);
  // Poll /api/pending-works/due-soon:
  //  - every 5 minutes for items due today
  //  - every 30 minutes for overdue / upcoming pending items
  const lastNormalAlert = useRef(0);
  const lastTodayAlert = useRef(0);
  useEffect(() => {
    if (!activeCompanyId || user?.role === "hr") return;
    let cancelled = false;
    const check = async () => {
      try {
        const { data } = await api.get("/pending-works/due-soon");
        if (cancelled) return;
        const now = Date.now();
        const dueToday = (data.due_today || []).length + (data.overdue || []).length;
        const upcoming = (data.upcoming || []).length;
        // Due-today or overdue → every 5 minutes
        if (dueToday > 0 && now - lastTodayAlert.current >= 5 * 60 * 1000) {
          lastTodayAlert.current = now;
          toast.warning(
            `${dueToday} work${dueToday > 1 ? "s" : ""} due today — complete before deadline!`,
            {
              duration: 8000,
              action: {
                label: "Open",
                onClick: () => navigate("/pending-works"),
              },
            }
          );
        }
        // Otherwise → every 30 minutes for upcoming pending
        else if (upcoming > 0 && now - lastNormalAlert.current >= 30 * 60 * 1000) {
          lastNormalAlert.current = now;
          toast.info(
            `Reminder: ${upcoming} pending work item${upcoming > 1 ? "s" : ""} awaiting completion`,
            {
              duration: 6000,
              action: {
                label: "Open",
                onClick: () => navigate("/pending-works"),
              },
            }
          );
        }
      } catch { /* ignore */ }
    };
    // Fire once shortly after mount, then every 60 seconds re-evaluate
    const initial = setTimeout(check, 8000);
    const timer = setInterval(check, 60 * 1000);
    return () => { cancelled = true; clearTimeout(initial); clearInterval(timer); };
  }, [activeCompanyId, navigate, user?.role]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const companySelect = (
    <Select value={activeCompanyId || ""} onValueChange={(v) => setActiveCompanyId(v)}>
      <SelectTrigger className="h-9 w-full md:w-[240px]" data-testid="company-selector">
        <SelectValue placeholder="Select company" />
      </SelectTrigger>
      <SelectContent>
        {companies.map((c) => (
          <SelectItem key={c.id} value={c.id} data-testid={`company-option-${c.id}`}>
            {c.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  // Store switcher — admins (and managers WITHOUT an assigned store) can pick
  // "All Stores" or any single store. Employees AND managers explicitly
  // assigned to a store are locked to their store, shown as a read-only badge.
  const activeStoreObj = stores?.find((s) => s.id === activeStoreId);
  const lockedToStore = Boolean(user?.store_id) &&
    (user?.role === "manager" || user?.role === "employee");
  const canSwitchStore = user?.role === "admin" ||
    (user?.role === "manager" && !user?.store_id) ||
    user?.role === "regional_manager";
  // Regional managers can only switch between the stores in their region — the rest are hidden.
  const visibleStores = user?.role === "regional_manager"
    ? (stores || []).filter((s) => (user?.store_ids || []).includes(s.id))
    : stores;
  const storeSelect = canSwitchStore && visibleStores && visibleStores.length > 0 ? (
    <Select value={activeStoreId || "all"} onValueChange={(v) => setActiveStoreId(v)}>
      <SelectTrigger className="h-9 w-full md:w-[200px]" data-testid="store-selector">
        <SelectValue placeholder="All Stores" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all" data-testid="store-option-all">All Stores</SelectItem>
        {visibleStores.filter((s) => s.active !== false).map((s) => (
          <SelectItem key={s.id} value={s.id} data-testid={`store-option-${s.id}`}>
            {s.city} — {s.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  ) : (activeStoreObj ? (
    <div className="h-9 px-3 flex items-center rounded-md border border-border bg-muted/40 text-[12.5px] font-medium" data-testid="store-badge">
      <Storefront size={14} className="mr-1.5 text-primary" />
      {activeStoreObj.city} — {activeStoreObj.name}
    </div>
  ) : null);

  const userMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon" className="h-9 w-9 rounded-full" data-testid="user-menu-btn">
          <UserCircle weight="fill" size={22} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
          <SignOut size={16} className="mr-2" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="h-screen flex bg-background text-foreground overflow-hidden">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 border-r border-border bg-card sticky top-0 h-screen flex-col">
        <div className="h-20 px-4 flex items-center justify-center border-b border-border">
          <VetrichLogo size={52} tone="dark" data-testid="sidebar-brand-logo" />
        </div>
        <NavList user={user} can={can} isGstCompany={isGstCompany} />
        <div className="border-t border-border p-4 text-[11px] text-muted-foreground">
          <div className="font-heading font-semibold text-foreground tracking-tight">Smart Accounting.</div>
          <div className="text-muted-foreground">Smarter Business.</div>
        </div>
      </aside>

      {/* Mobile slide-out sidebar */}
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent side="left" className="w-72 p-0 flex flex-col md:hidden">
          <div className="h-16 px-4 flex items-center border-b border-border">
            <VetrichLogo size={42} tone="dark" />
          </div>
          <NavList user={user} can={can} isGstCompany={isGstCompany} onNavigate={() => setMobileNavOpen(false)} />
          <div className="border-t border-border p-4 text-[11px] text-muted-foreground">
            <div className="font-heading font-semibold text-foreground tracking-tight">Smart Accounting.</div>
            <div className="text-muted-foreground">Smarter Business.</div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar */}
        <header className="md:hidden h-14 border-b border-border bg-card px-3 flex items-center gap-2 sticky top-0 z-20">
          <Button variant="outline" size="icon" className="h-9 w-9" data-testid="mobile-nav-toggle"
            onClick={() => setMobileNavOpen(true)}>
            <List size={20} />
          </Button>
          <div className="flex-1 min-w-0">
            {companySelect}
          </div>
          {userMenu}
        </header>

        {/* Desktop top bar */}
        <header className="hidden md:flex h-16 border-b border-border bg-card px-6 items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-4">
            <span className="eyebrow">Active Company</span>
            {companySelect}
            {storeSelect && (
              <>
                <span className="eyebrow ml-2">Store</span>
                {storeSelect}
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="font-heading text-[13px] font-semibold leading-tight">{user?.name}</div>
              <div className="text-[11px] text-muted-foreground uppercase tracking-wider">{user?.role?.replace("_", " ")}</div>
            </div>
            {userMenu}
          </div>
        </header>

        <main className="flex-1 overflow-y-auto overflow-x-hidden" data-testid="main-scroll" style={{ scrollBehavior: "smooth", overscrollBehavior: "contain" }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
